import { useEffect, useRef, useState } from 'react';

import {
  buildDiagnosticsExport,
  isRunningInNativeApp,
  native,
  validateDiagnostics,
} from '@muzly/shared';
import type {
  DiagnosticCheckResult,
  DiagnosticModule,
  DiagnosticSeverity,
  DiagnosticsSnapshot,
  DiagnosticsValidationReport,
  RealtimePerformanceMetrics,
  ThermalState,
} from '@muzly/shared';

import { InfoTip } from './InfoTip';
import { useSessionControl } from './sessionControlContext';
import { STATUS_TONE } from './statusTone';

/**
 * 진단/성능 패널 — `getDiagnosticsSnapshot`을 패널이 보일 때만 ~1s polling 한다.
 *
 * 스냅샷 RPC는 브라우저 mock 모듈에서만 직접 호출 가능하다(네이티브 셸의 WebView
 * RPC 표면 `NativeMethods`에는 아직 노출 안 됨). 그래서:
 *   - 브라우저(mock): **세션이 쓰는** mock 모듈의 `getDiagnosticsSnapshot()`를 1s 폴링 → `@muzly/shared`
 *     `validateDiagnostics`(#38 룰엔진)로 모듈별 pass/warn/fail 판정.
 *   - 네이티브: 스냅샷 폴링이 없으므로 `performance_metrics` push로 폴백(카드만, 배지 없음).
 *
 * 불변식: 지표/스냅샷은 polling, 이산 사건은 push — raw PCM은 건너오지 않는다.
 */

const POLL_INTERVAL_MS = 1000;
// 폴링/푸시 주기(≈1s)의 2.5배 이상 지나면 stale로 본다.
const STALE_AGE_MS = 2500;
// hop=1024 @ 48kHz → 프레임 예산 ≈ 21.3ms. P95가 이 안에 들어와야 따라간다.
const BUDGET_P95_MS = 21;
// DiagnosticsExport time-series 버퍼 상한(≈10분 @1s) — 무한 증가 방지.
const MAX_EXPORT_SAMPLES = 600;

type DiagnosticsView = {
  source: 'snapshot' | 'metrics';
  timestampMs: number;
  analysisAvgMs: number | null;
  analysisP95Ms: number | null;
  deadlineMissCount: number;
  ringOverrunCount: number;
  inputDropoutCount: number;
  frameDroppedCount: number;
  queueDepth: number;
  thermalState?: ThermalState;
  degradation?: { level: number; reason: string; action: string };
};

const fromSnapshot = (s: DiagnosticsSnapshot): DiagnosticsView => ({
  source: 'snapshot',
  timestampMs: s.timestampMs,
  analysisAvgMs: s.analysis.avgProcessMs,
  analysisP95Ms: s.analysis.p95ProcessMs,
  deadlineMissCount: s.analysis.deadlineMissCount,
  ringOverrunCount: s.ringBuffer.overrunCount,
  inputDropoutCount: s.audioInput.inputDropoutCount,
  frameDroppedCount: s.eventDispatcher.droppedPitchFrameCount,
  queueDepth: s.eventDispatcher.eventQueueDepth,
  thermalState: s.platform.thermalState,
  degradation: {
    level: s.degradation.level,
    reason: s.degradation.reason,
    action: s.degradation.action,
  },
});

const fromMetrics = (m: RealtimePerformanceMetrics): DiagnosticsView => ({
  source: 'metrics',
  timestampMs: m.timestampMs,
  analysisAvgMs: m.analysisAvgMs,
  analysisP95Ms: m.analysisP95Ms,
  deadlineMissCount: m.analysisDeadlineMissCount,
  ringOverrunCount: m.ringBufferOverrunCount,
  inputDropoutCount: m.inputDropoutCount,
  frameDroppedCount: m.pitchFrameDroppedCount,
  queueDepth: m.eventQueueDepth,
  thermalState: m.thermalState,
});

const isThermalHot = (state?: ThermalState): boolean =>
  state === 'serious' || state === 'critical';

const deviceOf = (
  s: DiagnosticsSnapshot,
): DiagnosticsValidationReport['device'] => ({
  platform: s.platform.platform,
  model: s.platform.deviceModel,
  osVersion: s.platform.osVersion,
});

const fmt = (n: number | null, d: number, suffix = ''): string =>
  typeof n === 'number' && Number.isFinite(n)
    ? `${n.toFixed(d)}${suffix}`
    : '—';

const Metric = ({
  label,
  value,
  tip,
  alert = false,
}: {
  label: string;
  value: string;
  /** "?" 툴팁: 이 지표가 무엇인지/정상값이 무엇인지 KO 평이체 한 줄(#17). */
  tip: string;
  alert?: boolean;
}) => (
  <div
    className={`flex flex-col gap-0.5 rounded-md border p-2 ${
      alert ? 'border-destructive bg-destructive/10' : 'border-border bg-card'
    }`}
  >
    <span className="flex items-center gap-1">
      <span className="text-xs text-muted-foreground">{label}</span>
      <InfoTip label={`${label} 설명`}>{tip}</InfoTip>
    </span>
    <span
      className={`font-mono text-sm ${alert ? 'text-destructive' : 'text-foreground'}`}
    >
      {value}
    </span>
  </div>
);

// pass/warn/fail 색의 의미 범례(#17) — 배지/카드 색이 무엇을 뜻하는지.
const SEVERITY_LEGEND: { severity: DiagnosticSeverity; text: string }[] = [
  { severity: 'pass', text: '정상 — 기준 안' },
  { severity: 'warn', text: '주의 — 예산 근접' },
  { severity: 'fail', text: '위험 — 예산 초과·끊김' },
];

const SeverityLegend = () => (
  <div className="flex flex-wrap items-center gap-1.5">
    <span className="text-[10px] uppercase text-muted-foreground">범례</span>
    {SEVERITY_LEGEND.map(({ severity, text }) => (
      <span
        key={severity}
        className={`flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] ${SEVERITY_STYLE[severity]}`}
      >
        <span className="font-mono font-semibold uppercase">{severity}</span>
        <span>{text}</span>
      </span>
    ))}
  </div>
);

// pass/warn/fail → 공유 상태색 토큰(정상=green/경고=amber/위험=red)에 매핑(#20 통일).
const SEVERITY_STYLE: Record<DiagnosticSeverity, string> = {
  pass: STATUS_TONE.ok,
  warn: STATUS_TONE.warn,
  fail: STATUS_TONE.danger,
};

// 모듈별 짧은 라벨(배지 표시용) — 룰엔진 모듈 키와 1:1.
const MODULE_LABELS: Record<DiagnosticModule, string> = {
  session: 'session',
  audio_input: 'input',
  ring_buffer: 'ring',
  analysis: 'analysis',
  detector: 'detector',
  post_processor: 'post',
  event_dispatcher: 'dispatch',
  ui_bridge: 'bridge',
  degradation: 'degrade',
  platform: 'platform',
};

const MODULE_ORDER = Object.keys(MODULE_LABELS) as DiagnosticModule[];

const RANK: Record<DiagnosticSeverity, number> = { pass: 0, warn: 1, fail: 2 };

type ModuleBadge = {
  module: DiagnosticModule;
  severity: DiagnosticSeverity;
  detail: string;
};

// 룰엔진 report.checks를 모듈별 최악 severity 배지로 접는다(같은 모듈 여러 체크 → 1배지).
const foldByModule = (report: DiagnosticsValidationReport): ModuleBadge[] => {
  const byModule = new Map<DiagnosticModule, DiagnosticCheckResult[]>();
  for (const check of report.checks) {
    const list = byModule.get(check.module) ?? [];
    list.push(check);
    byModule.set(check.module, list);
  }
  return MODULE_ORDER.flatMap((module) => {
    const checks = byModule.get(module);
    if (checks == null || checks.length === 0) {
      return [];
    }
    const severity = checks.reduce<DiagnosticSeverity>(
      (acc, c) => (RANK[c.severity] > RANK[acc] ? c.severity : acc),
      'pass',
    );
    const detail = checks
      .filter((c) => c.severity === severity)
      .map((c) => c.message)
      .join(' · ');
    return [{ module, severity, detail }];
  });
};

const ModuleBadgeChip = ({ badge }: { badge: ModuleBadge }) => (
  <span
    title={badge.detail}
    className={`flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] ${
      SEVERITY_STYLE[badge.severity]
    }`}
  >
    <span className="uppercase opacity-70">{MODULE_LABELS[badge.module]}</span>
    <span className="font-semibold uppercase">{badge.severity}</span>
  </span>
);

type ExportBuffer = {
  snapshots: DiagnosticsSnapshot[];
  reports: DiagnosticsValidationReport[];
};

// 누적 스냅샷/판정 time-series를 DiagnosticsExport JSON으로 내려받는다(IO만, 판정은 순수).
// 포맷·thresholdVersion 스탬프는 @muzly/shared `buildDiagnosticsExport`(스펙 §8)에 위임.
const downloadExport = (buffer: ExportBuffer) => {
  const latest = buffer.snapshots[buffer.snapshots.length - 1];
  const payload = buildDiagnosticsExport({
    ...buffer,
    device: deviceOf(latest) ?? { platform: 'ios' },
  });
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: 'application/json',
  });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `muzly-diagnostics-${payload.endedAtMs}.json`;
  anchor.click();
  URL.revokeObjectURL(url);
};

export const DiagnosticsPanel = () => {
  const isNative = isRunningInNativeApp();
  const { getDiagnostics } = useSessionControl();
  const [view, setView] = useState<DiagnosticsView | null>(null);
  const [report, setReport] = useState<DiagnosticsValidationReport | null>(
    null,
  );
  // export용 time-series 버퍼(렌더와 무관 — 다운로드 시점에만 읽음).
  const bufferRef = useRef<ExportBuffer>({ snapshots: [], reports: [] });
  const [sampleCount, setSampleCount] = useState(0);
  // stale 여부를 주기적으로 재계산하기 위한 벽시계 틱(스트림이 멈춰도 갱신).
  const [nowMs, setNowMs] = useState(() => Date.now());

  // 브라우저(mock): 패널이 떠 있는 동안만 스냅샷을 1s 폴링 → #38 룰엔진으로 판정.
  useEffect(() => {
    if (isNative) {
      return;
    }
    let cancelled = false;
    const poll = () => {
      // 세션이 실제로 쓰는 모듈에서 받는다. 패널이 모듈을 새로 만들면 그 인스턴스는
      // 시작된 적이 없어 세션이 뭘 하든 같은 값만 낸다(예전엔 mock이 무조건
      // "running"이라 답해서 그 사실이 안 보였다).
      const pending = getDiagnostics();
      if (!pending) {
        return;
      }
      void pending.then((snapshot) => {
        if (cancelled) {
          return;
        }
        const next = validateDiagnostics(snapshot, {
          device: deviceOf(snapshot),
        });
        setView(fromSnapshot(snapshot));
        setReport(next);
        const buffer = bufferRef.current;
        buffer.snapshots.push(snapshot);
        buffer.reports.push(next);
        if (buffer.snapshots.length > MAX_EXPORT_SAMPLES) {
          buffer.snapshots.shift();
          buffer.reports.shift();
        }
        setSampleCount(buffer.snapshots.length);
      });
    };
    poll();
    const id = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [isNative, getDiagnostics]);

  // 네이티브: 스냅샷 폴링이 없으므로 performance_metrics push로 폴백(카드만).
  useEffect(() => {
    if (!isNative) {
      return;
    }
    return native.on('pitchTrackerEvent', (event) => {
      if (event.type === 'performance_metrics') {
        setView(fromMetrics(event.metrics));
        return;
      }
      if (event.type === 'session_stopped') {
        setView(null);
      }
    });
  }, [isNative]);

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  const overBudget = view != null && (view.analysisP95Ms ?? 0) >= BUDGET_P95_MS;
  const thermalHot = isThermalHot(view?.thermalState);
  const stale = view != null && nowMs - view.timestampMs > STALE_AGE_MS;
  const degraded = overBudget || thermalHot;
  const badges = report != null ? foldByModule(report) : null;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-sm font-medium text-foreground">진단/성능</span>
        <div className="flex items-center gap-2">
          {view != null && (
            <span className="rounded border border-border/60 px-1.5 py-0.5 font-mono text-[10px] uppercase text-muted-foreground">
              {view.source === 'snapshot' ? 'poll' : 'push'}
            </span>
          )}
          {stale && (
            <span className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 font-mono text-[10px] uppercase text-amber-600">
              stale
            </span>
          )}
          <span
            className={`font-mono text-xs ${
              degraded ? 'text-destructive' : 'text-muted-foreground'
            }`}
          >
            {view == null ? '대기' : degraded ? '예산 초과' : '정상'} · 예산{' '}
            {BUDGET_P95_MS}ms
          </span>
          <button
            type="button"
            disabled={sampleCount === 0}
            onClick={() => downloadExport(bufferRef.current)}
            className="rounded border border-border px-2 py-0.5 font-mono text-[10px] uppercase text-foreground hover:bg-muted disabled:opacity-40"
          >
            export{sampleCount > 0 ? ` (${sampleCount})` : ''}
          </button>
        </div>
      </div>

      {report != null && badges != null && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span
            className={`flex items-center gap-1 rounded border px-1.5 py-0.5 font-mono text-[10px] ${
              SEVERITY_STYLE[report.overall]
            }`}
          >
            <span className="uppercase opacity-70">overall</span>
            <span className="font-semibold uppercase">{report.overall}</span>
          </span>
          {badges.map((badge) => (
            <ModuleBadgeChip key={badge.module} badge={badge} />
          ))}
        </div>
      )}

      {view == null ? (
        <div className="rounded-md border border-border bg-muted p-3 text-sm text-muted-foreground">
          {isNative
            ? 'performance_metrics 대기 중 — 네이티브가 계측을 emit하면 표시됩니다.'
            : '스냅샷 폴링 대기 중 — 곧 mock 스냅샷이 표시됩니다.'}
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-3 gap-2">
            <Metric
              label="analysis avg"
              value={fmt(view.analysisAvgMs, 1, ' ms')}
              tip="한 프레임을 분석하는 데 걸린 평균 시간입니다. 예산 21ms 안이어야 실시간을 따라갑니다."
            />
            <Metric
              label="analysis p95"
              value={fmt(view.analysisP95Ms, 1, ' ms')}
              tip="느린 쪽 상위 5%의 분석 시간입니다. 21ms를 넘으면 끊김 위험이라 빨갛게 표시됩니다."
              alert={overBudget}
            />
            <Metric
              label="deadline miss"
              value={`${view.deadlineMissCount}`}
              tip="분석이 제한 시간을 넘긴 횟수입니다. 0이 정상이고, 늘면 소리가 끊깁니다."
              alert={view.deadlineMissCount > 0}
            />
            <Metric
              label="ring overrun"
              value={`${view.ringOverrunCount}`}
              tip="입력 버퍼가 넘쳐 소리 데이터를 잃은 횟수입니다. 0이 정상입니다."
              alert={view.ringOverrunCount > 0}
            />
            <Metric
              label="input dropout"
              value={`${view.inputDropoutCount}`}
              tip="마이크 입력이 끊긴 횟수입니다. 0이 정상이고, 늘면 마이크/권한을 확인하세요."
              alert={view.inputDropoutCount > 0}
            />
            <Metric
              label="frame dropped"
              value={`${view.frameDroppedCount}`}
              tip="처리하지 못해 버린 프레임 수입니다. 0이 정상이고, 늘면 분석이 밀리는 중입니다."
            />
            <Metric
              label="queue depth"
              value={`${view.queueDepth}`}
              tip="처리를 기다리는 이벤트 수입니다. 계속 쌓이면 표시가 실시간보다 늦어집니다."
            />
            <Metric
              label="thermal"
              value={view.thermalState ?? '—'}
              tip="기기 발열 상태입니다. serious·critical이면 발열로 성능이 제한됩니다."
              alert={thermalHot}
            />
            <Metric
              label="degradation"
              value={
                view.degradation
                  ? `L${view.degradation.level} · ${view.degradation.reason}`
                  : '—'
              }
              tip="부하가 크면 엔진이 자동으로 품질을 낮추는 경감 단계입니다. L0이 정상, 숫자가 클수록 더 경감합니다."
              alert={(view.degradation?.level ?? 0) > 0}
            />
          </div>
          <SeverityLegend />
        </div>
      )}
    </section>
  );
};
